import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ConversationHistory, apiService } from '../services/api';
import { authService } from '../services/auth.service';

interface Conversation {
  id: string;
  title: string;
  timestamp: number;
  messageCount: number;
}

interface SidebarProps {
  activeConversation: string | null;
  pendingConversation: Conversation | null; // Unsaved "New Chat" conversation
  onNewChat: () => void;
  // Settings props
  executionMode: 'basic' | 'extended';
  onExecutionModeChange: (mode: 'basic' | 'extended') => void;
  personalityMode: 'simple' | 'professional' | 'teacher';
  onPersonalityModeChange: (mode: 'simple' | 'professional' | 'teacher') => void;
  onAdvancedSettingsOpen: () => void;
  // History props
  userId: string;
  projectKey: string | null;
  onHistoryConversationSelect?: (conversation: ConversationHistory) => void;
  refreshTrigger?: number; // Increment this to trigger a conversation history reload
  loadingConversations?: Set<string>; // Set of conversation IDs with pending requests
}

const Sidebar: React.FC<SidebarProps> = ({ 
  activeConversation,
  pendingConversation,
  onNewChat,
  executionMode,
  onExecutionModeChange,
  personalityMode,
  onPersonalityModeChange,
  onAdvancedSettingsOpen,
  userId,
  projectKey,
  onHistoryConversationSelect,
  refreshTrigger,
  loadingConversations
}) => {
  // Collapsed by default on small screens: the expanded 320px sidebar would
  // leave the chat (and its input field) only a sliver of a phone viewport.
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );
  const collapseOnMobile = () => {
    if (window.innerWidth < 768) setIsCollapsed(true);
  };
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isDevelopment, setIsDevelopment] = useState(false);

  // Fetch backend config to determine if we're in development mode
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:7003/api';
        const response = await fetch(`${apiUrl}/ie/config`);
        if (!response.ok) {
          throw new Error(`Config fetch failed: HTTP ${response.status}`);
        }
        const config = await response.json();
        setIsDevelopment(config.debug === true);
      } catch (err) {
        console.error('Failed to fetch config:', err);
        // Default to production mode if config fetch fails
        setIsDevelopment(false);
      }
    };

    fetchConfig();
  }, []);

  const handleLogout = async () => {
    if (!window.confirm('Are you sure you want to log out?')) {
      return;
    }

    setIsLoggingOut(true);
    try {
      await authService.logout();
      // Logout will redirect to Azure AD logout page
    } catch (error) {
      console.error('Logout failed:', error);
      alert('Logout failed. Please try again.');
      setIsLoggingOut(false);
    }
  };
  const [historyConversations, setHistoryConversations] = useState<ConversationHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // The history request can finish after the user has opened or created a chat.
  // The callback closes over the value from the render that started the request,
  // so it reads the latest value through this ref when it decides to auto-select.
  const activeConversationRef = useRef(activeConversation);
  activeConversationRef.current = activeConversation;

  const loadConversationHistory = useCallback(async () => {
    // Don't load conversation history if no project is selected yet
    if (!userId || !projectKey) {
      console.log('⏳ Waiting for project selection before loading conversation history...');
      return;
    }
    
    setHistoryLoading(true);
    setHistoryError(null);
    
    try {
      console.log(`✓ Loading conversation history for project: ${projectKey}`);
      // Use NEW multi-turn API endpoint
      const conversations = await apiService.getMultiTurnConversations(projectKey, 5);
      
      // Convert MultiTurnConversation[] to ConversationHistory[] format
      const formattedConversations: ConversationHistory[] = conversations.map(conv => ({
        id: parseInt(conv.id.replace('conv_', '')), // Convert string ID to number for compatibility
        user_id: conv.user_id,
        project_key: conv.project_key,
        session_id: conv.session_id || conv.id,
        query: conv.title || 'Conversation',
        response: `${conv.message_count} messages`,
        created_at: conv.created_at,
        updated_at: conv.last_message_at || conv.updated_at,
        metadata: {}
      }));
      
      setHistoryConversations(formattedConversations);
      
      // Auto-select the most recent conversation if none is active and there are conversations
      if (!activeConversationRef.current && formattedConversations.length > 0 && onHistoryConversationSelect) {
        const mostRecent = formattedConversations[0];
        console.log(`✓ Auto-selecting most recent conversation: ${mostRecent.id}`);
        onHistoryConversationSelect(mostRecent);
      }
    } catch (err) {
      console.error('Error loading conversation history:', err);
      setHistoryError('Failed to load conversation history');
    } finally {
      setHistoryLoading(false);
    }
  }, [userId, projectKey, activeConversation, onHistoryConversationSelect]);

  useEffect(() => {
    loadConversationHistory();
  }, [loadConversationHistory]);

  // Reload when refreshTrigger changes (used after creating a new conversation)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      console.log('🔄 Refreshing conversation history due to trigger change');
      loadConversationHistory();
    }
  }, [refreshTrigger, loadConversationHistory]);

  const formatHistoryTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = diffInMs / (1000 * 60);
    const diffInHours = diffInMs / (1000 * 60 * 60);
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${Math.floor(diffInMinutes)}m ago`;
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <>
      {/* Mobile backdrop: the expanded sidebar overlays the chat instead of squeezing it */}
      {!isCollapsed && (
        <div
          className="md:hidden absolute inset-0 z-20 bg-ie-navy/50"
          onClick={() => setIsCollapsed(true)}
          aria-hidden="true"
        />
      )}
      <div className={`flex flex-col h-full bg-white border-r border-ie-rule transition-all duration-300 ${
        isCollapsed
          ? 'w-0 border-r-0 md:w-16 md:border-r'
          : 'w-80 max-w-[85vw] absolute inset-y-0 left-0 z-30 shadow-lift md:relative md:inset-auto md:z-auto md:max-w-none md:shadow-none'
      }`}>
      {/* Header */}
      <div className={`${isCollapsed ? 'p-0 md:p-4 md:border-b' : 'p-4 border-b'} border-ie-rule`}>
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <h2 className="font-condensed text-xl font-semibold text-ie-navy">Conversations</h2>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand conversation list' : 'Collapse conversation list'}
            className={`p-2 text-ie-slate hover:text-ie-navy hover:bg-ie-mist rounded-full transition-colors ${
              isCollapsed
                ? 'absolute top-3 left-3 z-20 flex items-center gap-1.5 pl-3 pr-4 h-10 bg-white text-ie-navy border border-ie-navy shadow-lift md:static md:z-auto md:h-auto md:p-2 md:border-0 md:shadow-none md:bg-transparent md:text-ie-slate'
                : ''
            }`}
          >
            <span className={`md:hidden font-condensed text-sm font-semibold ${isCollapsed ? '' : 'hidden'}`}>Chats</span>
            <svg 
              className={`w-5 h-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} 
              fill="currentColor" 
              viewBox="0 0 20 20"
            >
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
        
        {!isCollapsed && (
          <button
            onClick={() => {
              onNewChat();
              collapseOnMobile();
            }}
            className="w-full mt-3 h-11 bg-ie-navy text-white border border-ie-navy rounded-pill hover:bg-ie-sky hover:text-ie-navy transition-colors duration-300 font-condensed font-medium text-sm uppercase tracking-wide flex items-center justify-center space-x-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            <span>New Chat</span>
          </button>
        )}
        
        {isCollapsed && (
          <button
            onClick={onNewChat}
            className="hidden md:flex w-full mt-3 h-11 bg-ie-navy text-white border border-ie-navy rounded-pill hover:bg-ie-sky hover:text-ie-navy transition-colors duration-300 items-center justify-center"
            title="New Chat"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      {/* Basic Settings - Only show in development mode */}
      {!isCollapsed && isDevelopment && (
        <div className="p-3 border-b border-ie-rule">
          <h3 className="text-sm font-medium text-ie-navy mb-3">Settings</h3>
          
          {/* Execution Mode */}
          <div className="mb-3">
            <label htmlFor="execution-mode" className="block text-xs text-ie-slate mb-1">Mode</label>
            <select
              id="execution-mode"
              value={executionMode}
              onChange={(e) => onExecutionModeChange(e.target.value as 'basic' | 'extended')}
              className="w-full text-sm bg-white border border-ie-navy rounded-lg px-2 py-1"
            >
              <option value="basic">Basic (Fast)</option>
              <option value="extended">Extended (Thorough)</option>
            </select>
          </div>
          
          {/* Personality Mode */}
          <div className="mb-3">
            <label htmlFor="personality-mode" className="block text-xs text-ie-slate mb-1">Style</label>
            <select
              id="personality-mode"
              value={personalityMode}
              onChange={(e) => onPersonalityModeChange(e.target.value as 'simple' | 'professional' | 'teacher')}
              className="w-full text-sm bg-white border border-ie-navy rounded-lg px-2 py-1"
            >
              <option value="simple">Simple</option>
              <option value="professional">Professional</option>
              <option value="teacher">Educational</option>
            </select>
          </div>
          
          {/* Advanced Settings Button */}
          <button
            onClick={onAdvancedSettingsOpen}
            className="w-full text-sm text-ie-navy hover:bg-ie-mist px-3 py-2 rounded-pill border border-ie-navy transition-colors duration-200 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
            </svg>
            <span>Advanced</span>
          </button>
        </div>
      )}
      
      {/* User Profile and Logout */}
      {!isCollapsed && (
        <div className="p-3 border-b border-ie-rule">
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className={`w-full text-sm px-3 py-2 rounded-pill border transition-colors duration-200 flex items-center justify-center space-x-2 ${
              isLoggingOut
                ? 'text-ie-fog bg-ie-pale border-ie-rule cursor-not-allowed'
                : 'text-ie-alert hover:bg-ie-alert hover:text-white border-ie-alert'
            }`}
          >
            {isLoggingOut ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-ie-slate"></div>
                <span>Logging out...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                </svg>
                <span>Logout</span>
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto p-2">
        {/* All Conversations (from backend history) */}
        {!isCollapsed && (
          <div className="space-y-2">
            <h3 className="font-condensed text-sm font-semibold text-ie-slate mb-2 px-1">Conversations</h3>
            
            {/* Conversation list - scrolling handled by parent flex container */}
            <div className="space-y-2">
              {/* Pending unsaved conversation (New Chat) */}
              {pendingConversation && (
                <button
                  onClick={() => {}} // Already active, no action needed
                  className="w-full p-3 rounded-lg mb-2 text-left bg-ie-mist border border-ie-sky"
                  title="New unsaved conversation"
                >
                  <div>
                    <h3 className="font-medium text-sm truncate text-ie-navy flex items-center">
                      {loadingConversations?.has(pendingConversation.id) ? (
                        <svg className="animate-spin h-3 w-3 mr-2 text-ie-cyan" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" />
                        </svg>
                      )}
                      {pendingConversation.title}
                    </h3>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-xs text-ie-steel italic">
                        Not saved yet
                      </span>
                      <span className="text-xs text-ie-steel">
                        Just now
                      </span>
                    </div>
                  </div>
                </button>
              )}
              
              {historyLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ie-cyan mx-auto"></div>
                  <p className="text-xs text-ie-slate mt-2">Loading history...</p>
                </div>
              ) : historyError ? (
                <div className="text-center py-4">
                  <p className="text-xs text-ie-alert">{historyError}</p>
                </div>
              ) : historyConversations.length === 0 && !pendingConversation ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 bg-ie-mist rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-ie-slate" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm text-ie-slate">No conversations yet</p>
                  <p className="text-xs text-ie-slate mt-1">Start a new chat to begin</p>
                </div>
              ) : (
                historyConversations.map((conversation, index) => {
                  const isLoading = loadingConversations?.has(conversation.session_id) || false;
                  return (
                    <button
                      key={`history-${conversation.id}-${index}`}
                      onClick={() => {
                        onHistoryConversationSelect?.(conversation);
                        collapseOnMobile();
                      }}
                      className={`w-full p-3 rounded-lg mb-2 text-left transition-all duration-200 border ${
                        activeConversation === conversation.session_id
                          ? 'bg-ie-mist border-ie-sky'
                          : 'hover:bg-ie-pale border-transparent'
                      } group`}
                    >
                      <div>
                        <h3 className={`font-medium text-sm truncate flex items-center ${
                          activeConversation === conversation.session_id 
                            ? 'text-ie-navy' 
                            : 'text-ie-navy'
                        }`}>
                          {isLoading && (
                            <svg className="animate-spin h-3 w-3 mr-2 text-ie-cyan" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          {conversation.query || 'Untitled conversation'}
                        </h3>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-ie-slate">
                            {formatHistoryTimestamp(conversation.updated_at || conversation.created_at)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default Sidebar;